import { supabase } from './supabase';
import { PostgrestError } from '@supabase/supabase-js';
import { Listing, mapPublicRow } from './listings';
import { getIsAuthenticated } from './auth';

export interface SearchFilters {
  query?: string;
  brand?: string[];
  condition?: string;
  minPrice?: number;
  maxPrice?: number;
  type?: 'sale' | 'trade' | 'both';
  color?: string;
  sort?: 'newest' | 'price_asc' | 'price_desc' | 'discount';
  source?: 'user' | 'clearancewigs';
  fiber?: string;
  capSize?: string;
}

export interface SearchPagination {
  limit?: number;
  offset?: number;
}

export interface SearchResult {
  listings: Listing[];
  total: number;
  error: PostgrestError | null;
}

export async function searchListings(
  filters: SearchFilters = {},
  pagination: SearchPagination = {}
): Promise<SearchResult> {
  const isAuthenticated = await getIsAuthenticated();
  const limit = pagination.limit ?? 24;
  const offset = pagination.offset ?? 0;
  const selectedBrands = filters.brand || [];

  // ------- Public RPC path (Phase 1 de-gate) -------
  // Also used when signed in for the 'discount' sort, which is computed
  // server-side in the RPC and can't be expressed as a PostgREST order.
  if (!isAuthenticated || filters.sort === 'discount') {
    // The RPC takes a single brand. For 0 or 1 selected brand we can paginate
    // server-side. For a multi-brand selection (rare) we fetch a wide page with
    // no brand filter and narrow + paginate client-side.
    if (selectedBrands.length <= 1) {
      const { data, error } = await supabase.rpc('get_public_listings', {
        p_brand: selectedBrands[0] ?? null,
        p_search: filters.query ?? null,
        p_color: filters.color ?? null,
        p_min: filters.minPrice ?? null,
        p_max: filters.maxPrice ?? null,
        p_sort: filters.sort ?? 'newest',
        p_condition: filters.condition ?? null,
        p_type: filters.type ?? null,
        p_limit: limit,
        p_offset: offset,
        p_source: filters.source ?? null,
        p_fiber: filters.fiber ?? null,
        p_cap_size: filters.capSize ?? null,
      });
      if (error) {
        console.error('Error searching public listings:', error);
        return { listings: [], total: 0, error };
      }
      const rows = data || [];
      const total = rows.length > 0 ? Number(rows[0].total_count) : 0;
      return { listings: rows.map(mapPublicRow), total, error: null };
    }

    const { data, error } = await supabase.rpc('get_public_listings', {
      p_brand: null,
      p_search: filters.query ?? null,
      p_color: filters.color ?? null,
      p_min: filters.minPrice ?? null,
      p_max: filters.maxPrice ?? null,
      p_sort: filters.sort ?? 'newest',
      p_condition: filters.condition ?? null,
      p_type: filters.type ?? null,
      p_limit: 200,
      p_offset: 0,
      p_source: filters.source ?? null,
      p_fiber: filters.fiber ?? null,
      p_cap_size: filters.capSize ?? null,
    });
    if (error) {
      console.error('Error searching public listings:', error);
      return { listings: [], total: 0, error };
    }
    const filtered = (data || []).filter((r: any) => selectedBrands.includes(r.brand));
    const total = filtered.length;
    const page = filtered.slice(offset, offset + limit).map(mapPublicRow);
    return { listings: page, total, error: null };
  }

  // ------- Authenticated: full embed with server-side pagination + count -------
  let query = supabase
    .from('listings')
    .select('*, owner:listing_owners!user_id(*)', { count: 'exact' })
    .eq('status', 'active');

  if (filters.query) {
    // Every word must match somewhere (multiple .or() calls AND together).
    for (const word of filters.query.trim().split(/\s+/).filter(Boolean)) {
      const term = `%${word}%`;
      query = query.or(`title.ilike.${term},description.ilike.${term},brand.ilike.${term}`);
    }
  }

  if (selectedBrands.length > 0) {
    query = query.in('brand', selectedBrands);
  }

  if (filters.condition) {
    query = query.eq('condition', filters.condition);
  }

  if (filters.type) {
    query = query.eq('type', filters.type);
  }

  if (filters.color) {
    query = query.eq('color', filters.color);
  }

  if (filters.source) {
    query = query.eq('source', filters.source);
  }

  if (filters.fiber) {
    query = query.eq('fiber_type', filters.fiber);
  }

  if (filters.capSize) {
    query = query.eq('cap_size', filters.capSize);
  }

  if (filters.minPrice) {
    query = query.gte('price', parseFloat(filters.minPrice.toString()));
  }

  if (filters.maxPrice) {
    query = query.lte('price', parseFloat(filters.maxPrice.toString()));
  }

  switch (filters.sort) {
    case 'price_asc':
      query = query.order('price', { ascending: true });
      break;
    case 'price_desc':
      query = query.order('price', { ascending: false });
      break;
    default:
      query = query.order('created_at', { ascending: false });
  }

  query = query.range(offset, offset + limit - 1);

  const { data, error, count } = await query;
  return { listings: data || [], total: count ?? 0, error };
}
